package com.qrkontaq.data.dto

import kotlinx.serialization.Serializable

// --- Auth ---
// Admin auth is OAuth-only (Google/Apple via the auth.qrkontaq.com popup); the token
// pair arrives by postMessage, so there are no login/register request DTOs here. Only
// the silent token refresh is done from this client:

/** Body for POST /v1/auth/refresh. */
@Serializable
data class RefreshTokenRequestDto(
    val refreshToken: String,
)

/** Payload of the refresh response — a rotated access/refresh pair. */
@Serializable
data class TokenPairDto(
    val accessToken: String,
    val refreshToken: String,
)

// --- Restaurants (admin) ---

@Serializable
data class AdminRestaurantDto(
    val id: String,
    val slug: String,
    val name: String,
    val description: Map<String, String> = emptyMap(),
    val logoUrl: String? = null,
    val coverUrl: String? = null,
    val theme: String = "default",
    val menuCategoryThumbs: Boolean = true,
    val menuFeatureFirst: Boolean = true,
    val menuShowPopular: Boolean = true,
    val menuShowChef: Boolean = true,
    val menuShowFilters: Boolean = true,
    val accentColor: String? = null,
    val address: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    val phone: String? = null,
    val socials: Map<String, String> = emptyMap(),
    val workingHours: Map<String, DayHoursDto?> = emptyMap(),
    val currency: String = "EUR",
    val languages: List<String> = listOf("uk"),
    val defaultLanguage: String = "uk",
    val status: String = "ACTIVE",
    val createdAt: String = "",
    val loyaltyEnabled: Boolean = false,
    val loyaltyRatePercent: Int = 5,
    val guestCheckEnabled: Boolean = true,
    val waiterCallEnabled: Boolean = true,
    /** Whether the venue's subscription currently unlocks Pro features. */
    val isPro: Boolean = false,
)

@Serializable
data class MyRestaurantDto(
    val restaurant: AdminRestaurantDto,
    val role: String,
)

@Serializable
data class CreateRestaurantRequestDto(
    val name: String,
    val slug: String,
    val address: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    /** Owner's language; the backend seeds starter content in it. */
    val language: String? = null,
)

@Serializable
data class UpdateRestaurantRequestDto(
    val name: String? = null,
    val description: Map<String, String>? = null,
    val logoUrl: String? = null,
    val coverUrl: String? = null,
    val theme: String? = null,
    val menuCategoryThumbs: Boolean? = null,
    val menuFeatureFirst: Boolean? = null,
    val menuShowPopular: Boolean? = null,
    val menuShowChef: Boolean? = null,
    val menuShowFilters: Boolean? = null,
    val accentColor: String? = null,
    val address: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    val phone: String? = null,
    val socials: Map<String, String>? = null,
    val workingHours: Map<String, DayHoursDto?>? = null,
    val currency: String? = null,
    val languages: List<String>? = null,
    val defaultLanguage: String? = null,
    val loyaltyEnabled: Boolean? = null,
    val loyaltyRatePercent: Int? = null,
    val guestCheckEnabled: Boolean? = null,
    val waiterCallEnabled: Boolean? = null,
)

// --- Menu (admin) ---

@Serializable
data class AdminMenuItemDto(
    val id: String,
    val categoryId: String,
    val name: Map<String, String>,
    val description: Map<String, String> = emptyMap(),
    val slug: String,
    val priceMinor: Int,
    val photoUrl: String? = null,
    val weight: String? = null,
    val tags: List<String> = emptyList(),
    val allergens: List<String> = emptyList(),
    val calories: Int? = null,
    val isFeatured: Boolean = false,
    val isAvailable: Boolean = true,
    val sort: Int = 0,
)

@Serializable
data class AdminMenuCategoryBlockDto(
    val category: MenuCategoryDto,
    val items: List<AdminMenuItemDto>,
)

@Serializable
data class AdminMenuDto(
    val categories: List<AdminMenuCategoryBlockDto>,
)

@Serializable
data class CreateCategoryRequestDto(
    val name: Map<String, String>,
    val isBar: Boolean = false,
)

@Serializable
data class UpdateCategoryRequestDto(
    val name: Map<String, String>? = null,
    val isVisible: Boolean? = null,
    val isBar: Boolean? = null,
)

@Serializable
data class CreateItemRequestDto(
    val categoryId: String? = null,
    val name: Map<String, String>,
    val description: Map<String, String> = emptyMap(),
    val priceMinor: Int,
    val photoUrl: String? = null,
    val weight: String? = null,
    val tags: List<String> = emptyList(),
    val allergens: List<String> = emptyList(),
    val calories: Int? = null,
    val isFeatured: Boolean = false,
)

@Serializable
data class UpdateItemRequestDto(
    val categoryId: String? = null,
    val name: Map<String, String>? = null,
    val description: Map<String, String>? = null,
    val priceMinor: Int? = null,
    val photoUrl: String? = null,
    val weight: String? = null,
    val tags: List<String>? = null,
    val allergens: List<String>? = null,
    val calories: Int? = null,
    val isFeatured: Boolean? = null,
)

@Serializable
data class AvailabilityRequestDto(
    val isAvailable: Boolean,
)

@Serializable
data class OrderEntryDto(
    val id: String,
    val sort: Int,
)

@Serializable
data class ReorderRequestDto(
    val order: List<OrderEntryDto>,
)

// --- Tables ---

@Serializable
data class TableDto(
    val id: String,
    val label: String,
    val posX: Double? = null,
    val posY: Double? = null,
)

@Serializable
data class CreateTableRequestDto(
    val label: String,
)

@Serializable
data class TablePositionDto(
    val id: String,
    val x: Double,
    val y: Double,
)

@Serializable
data class TableLayoutRequestDto(
    val positions: List<TablePositionDto>,
)

// --- Staff ---

@Serializable
data class StaffMemberDto(
    val userId: String,
    val role: String,
    val fullName: String? = null,
    val email: String? = null,
    val avatarUrl: String? = null,
    val memberSince: String = "",
)

@Serializable
data class InviteDto(
    val id: String,
    val code: String,
    val url: String,
    val role: String,
    val expiresAt: String,
)

@Serializable
data class AcceptInviteResponseDto(
    val restaurantId: String = "",
    val role: String = "",
)

@Serializable
data class StaffDto(
    val members: List<StaffMemberDto>,
    val activeInvites: List<InviteDto>,
)

@Serializable
data class CreateInviteRequestDto(
    val role: String,
)

// --- Subscription / payments ---

@Serializable
data class SubscriptionDto(
    val plan: String? = null,
    val status: String,
    val paidUntil: String? = null,
    val autoRenew: Boolean = false,
    val cardMask: String? = null,
)

@Serializable
data class PaymentDto(
    val id: String,
    val plan: String,
    val amountMinor: Int,
    val currency: String,
    val status: String,
    val paidAt: String? = null,
    val createdAt: String,
)

@Serializable
data class SubscriptionInfoDto(
    val subscription: SubscriptionDto,
    val payments: List<PaymentDto>,
)

@Serializable
data class CheckoutRequestDto(
    val plan: String,
    val autoRenew: Boolean = false,
    val lang: String = "uk",
)

@Serializable
data class AutoRenewRequestDto(
    val enabled: Boolean,
)

@Serializable
data class CheckoutResponseDto(
    val paymentUrl: String,
)

// --- Stats ---

@Serializable
data class DayCountDto(
    val date: String,
    val count: Long,
)

@Serializable
data class TopItemDto(
    val item: AdminMenuItemDto,
    val likes: Long,
)

@Serializable
data class HourCountDto(
    val hour: Int,
    val count: Long,
)

@Serializable
data class LabelCountDto(
    val label: String,
    val count: Long,
)

@Serializable
data class SoldItemDto(
    val itemId: String? = null,
    val name: String,
    val qty: Long,
    val revenueMinor: Long,
)

@Serializable
data class StatsDto(
    val from: String,
    val to: String,
    val scansByDay: List<DayCountDto> = emptyList(),
    val scansByHour: List<HourCountDto> = emptyList(),
    val scansByTable: List<LabelCountDto> = emptyList(),
    val visits: Long = 0,
    val topItems: List<TopItemDto> = emptyList(),
    val soldItems: List<SoldItemDto> = emptyList(),
    val revenueMinor: Long = 0,
    val rating: RatingDto = RatingDto(),
)

// Personal work of one staff member: what they cooked / served themselves.
@Serializable
data class MyWorkItemDto(
    val name: String,
    val qty: Int,
)

@Serializable
data class MyWorkOrderDto(
    val orderId: String,
    val tableLabel: String,
    val items: List<MyWorkItemDto> = emptyList(),
    val at: String = "",
)

@Serializable
data class MyStatsDto(
    val from: String,
    val to: String,
    val cooked: List<MyWorkOrderDto> = emptyList(),
    val served: List<MyWorkOrderDto> = emptyList(),
    val taken: List<MyWorkOrderDto> = emptyList(),
)

// --- Waiter calls ---

@Serializable
data class WaiterCallDto(
    val id: String,
    val tableLabel: String,
    val type: String,
    val status: String,
    val createdAt: String,
)

@Serializable
data class MediaUploadResponseDto(
    val url: String,
)

// --- Orders ---

@Serializable
data class OrderItemDto(
    val id: String = "",
    val itemId: String? = null,
    val name: String,
    val priceMinor: Int,
    val qty: Int,
    val isBar: Boolean = false,
)

@Serializable
data class OrderDto(
    val id: String,
    val tableId: String,
    val tableLabel: String,
    val items: List<OrderItemDto> = emptyList(),
    val status: String, // NEW | COOKING | READY | SERVED | PAID
    val createdAt: String = "",
    val createdBy: String? = null,
)

@Serializable
data class OrderItemRequestDto(
    val itemId: String? = null,
    val name: String,
    val priceMinor: Int,
    val qty: Int,
)

@Serializable
data class CreateOrderRequestDto(
    val tableId: String,
    val items: List<OrderItemRequestDto>,
)

@Serializable
data class UpdateOrderStatusRequestDto(
    val status: String,
)

// --- Loyalty (cashback) ---

@Serializable
data class LoyaltyCandidateDto(
    val userId: String,
    val displayName: String = "",
    val avatarUrl: String? = null,
    val scannedAt: String = "",
    val balanceMinor: Int = 0,
    val visitCount: Int = 0,
    val lastVisitAt: String? = null,
    val calling: Boolean = false,
    val callType: String? = null,
    val callAt: String? = null,
)

@Serializable
data class LoyaltyCandidatesDto(
    val candidates: List<LoyaltyCandidateDto> = emptyList(),
)

@Serializable
data class LoyaltyIdentifyRequestDto(
    val code: String,
)

@Serializable
data class LoyaltyGuestDto(
    val userId: String = "",
    val displayName: String = "",
    val balanceMinor: Int = 0,
)

@Serializable
data class CloseTableRequestDto(
    val loyaltyCode: String? = null,
    val redeemMinor: Int? = null,
)

@Serializable
data class CloseTableLoyaltyDto(
    val earnedMinor: Int = 0,
    val redeemedMinor: Int = 0,
    val balanceMinor: Int = 0,
    val displayName: String = "",
)

@Serializable
data class CloseTableResponseDto(
    val loyalty: CloseTableLoyaltyDto? = null,
)
